import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { CapabilityChips } from '@/components/platform/CapabilityChips';
import { StateBadge } from '@/components/platform/StateBadge';
import { InstitutionName, InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { IntegrationsArea } from './IntegrationsArea';
import type {
  CreateInstitutionMappingRequest,
  InstitutionIntegrationMapping,
  IntegrationCapability,
  IntegrationRegistration,
  PlatformAuditRecord,
  PlatformConnection,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * Which record over there is THIS institution.
 *
 * THE INSTITUTION SELECTOR BELONGS HERE AND NOWHERE ELSE. It used to be the door
 * into the whole integrations area, which forced an environment-level question —
 * "is the Digital Switch up?" — to be asked about one school. Now it scopes only
 * the thing that is genuinely per-school: the identifiers that distinguish this
 * institution from every other institution reached down the same wire.
 *
 * TWO STATUSES, SIDE BY SIDE, ALWAYS. Every row shows the CONNECTION's health and
 * the MAPPING's status as separate facts, because the common case is that they
 * disagree: the Switch is HEALTHY, other schools are transacting through it, and
 * this school's tenant mapping has never been filled in. A screen that showed one
 * number would have to pick which of those two truths to hide.
 *
 * NOT EVERY ACTION HERE IS AN INSTITUTION ADMINISTRATOR'S. This console is
 * platform-admin only, and the actions on this screen are platform-admin actions:
 * an institution administrator cannot register a mapping into a shared connection
 * or retire one. Where that matters it is said on screen rather than left for
 * somebody to infer from a 403.
 */

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function InstitutionIntegrationsPage() {
  const { institutionId = '' } = useParams();
  const { t } = useTranslation();

  if (institutionId.length === 0) {
    return <InstitutionChooser />;
  }

  // A deep link can still carry anything — an old bookmark, a hand-edited URL, a
  // school's name. The API validates this segment as a UUID, so sending a non-id
  // through produces the raw "Validation failed (uuid is expected)" dialog. Catch
  // it here and answer with the chooser and a sentence in the operator's terms.
  if (!UUID_SHAPE.test(institutionId)) {
    return <InstitutionChooser rejected={institutionId} />;
  }

  return <InstitutionMappings institutionId={institutionId} title={t('platform.mappings.pageTitle')} />;
}

/**
 * The tab with no institution chosen yet.
 *
 * A separate state rather than an empty table, because "no institution selected"
 * and "this institution has no mappings" are different answers and the second one
 * is actionable.
 */
function InstitutionChooser({ rejected }: { rejected?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <IntegrationsArea
      active="mappings"
      title={t('platform.mappings.pageTitle')}
      lead={t('platform.mappings.pageLead')}
    >
      <Panel title={t('platform.mappings.chooseTitle')} icon="institution">
        {rejected && (
          <p className={styles.rowDetailError} role="alert">
            {t('platform.mappings.notAnInstitutionId', { value: rejected })}
          </p>
        )}
        {/*
          A name picker, NOT a text box. The old form navigated whatever the operator
          typed straight into the URL path, and the backend route validates that
          segment as a UUID — so typing the school's NAME (the only identifier an
          operator actually knows) produced "400 · Validation failed (uuid is
          expected)": an error in our vocabulary for a mistake in ours, not theirs.
          The picker searches the register by name and hands over the id itself;
          choosing is the submit, so a free-typed non-id can no longer reach the URL.
        */}
        <div className={styles.filterBar}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="mappings-institution">
              {t('platform.mappings.institutionLabel')}
            </label>
            <InstitutionPicker
              id="mappings-institution"
              value=""
              onChange={(next) => {
                if (next) navigate(`/admin/institutions/${encodeURIComponent(next)}/integrations`);
              }}
            />
          </div>
        </div>
        <p className={styles.sectionEmpty} role="status">
          {t('platform.mappings.chooseBody')}
        </p>
        <p className={styles.footnote}>
          <Link to="/admin/institutions">{t('platform.mappings.browseInstitutions')}</Link>
        </p>
      </Panel>
    </IntegrationsArea>
  );
}

function InstitutionMappings({ institutionId, title }: { institutionId: string; title: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [creating, setCreating] = useState(false);
  const [disabling, setDisabling] = useState<InstitutionIntegrationMapping | null>(null);
  const [historyFor, setHistoryFor] = useState<InstitutionIntegrationMapping | null>(null);

  const fetcher = useCallback(
    () => platformApi.institutionIntegrations.list(institutionId),
    [institutionId],
  );
  const { state, reload } = useAsyncData<InstitutionIntegrationMapping[]>(
    `institution-mappings:${institutionId}`,
    fetcher,
  );

  return (
    <IntegrationsArea
      active="mappings"
      institutionId={institutionId}
      title={title}
      lead={t('platform.mappings.pageLead')}
      actions={
        <button type="button" className={styles.primaryBtn} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          {t('platform.mappings.addMapping')}
        </button>
      }
    >
      <nav className={styles.breadcrumb} aria-label={t('platform.mappings.breadcrumbLabel')}>
        <Link to="/admin/institutions">{t('platform.nav.institutions')}</Link>
        <Icon name="chevronRight" size={13} />
        {/* The school's name, not its UUID — the id tells an operator nothing. */}
        <Link to={`/admin/institutions/${encodeURIComponent(institutionId)}`}>
          <InstitutionName institutionId={institutionId} />
        </Link>
        <Icon name="chevronRight" size={13} />
        <span>{t('platform.integrations.tab.mappings')}</span>
      </nav>

      <p className={styles.dataNotice}>
        <Icon name="noAccess" size={15} aria-hidden="true" />
        {t('platform.mappings.platformAdminNotice')}
      </p>

      <Panel title={t('platform.mappings.mappingsTitle')} icon="switch">
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.mappings.mappingsEmptyTitle')}
          emptyBody={t('platform.mappings.mappingsEmptyBody')}
        >
          {(data) => (
            <ul className={styles.rowList}>
              {data.map((mapping) => (
                <MappingRow
                  key={mapping.id}
                  institutionId={institutionId}
                  mapping={mapping}
                  locale={locale}
                  onChanged={reload}
                  onDisable={() => setDisabling(mapping)}
                  onHistory={() => setHistoryFor(mapping)}
                />
              ))}
            </ul>
          )}
        </AsyncPanel>
      </Panel>

      <LegacyRegistrationsPanel institutionId={institutionId} locale={locale} />

      <AddMappingDialog
        open={creating}
        institutionId={institutionId}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          reload();
        }}
      />

      <DisableMappingDialog
        mapping={disabling}
        institutionId={institutionId}
        onClose={() => setDisabling(null)}
        onDisabled={() => {
          setDisabling(null);
          reload();
        }}
      />

      <MappingHistoryDialog
        mapping={historyFor}
        institutionId={institutionId}
        locale={locale}
        onClose={() => setHistoryFor(null)}
      />
    </IntegrationsArea>
  );
}

/* ─── One mapping ─── */

function MappingRow({
  institutionId,
  mapping,
  locale,
  onChanged,
  onDisable,
  onHistory,
}: {
  institutionId: string;
  mapping: InstitutionIntegrationMapping;
  locale: string;
  onChanged: () => void;
  onDisable: () => void;
  onHistory: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  const connection = mapping.connection;

  /**
   * Verification IS the institution-level access test.
   *
   * There is one call, not two, because there is one question: does the far end,
   * reached over the shared connection, recognise the identifiers recorded for
   * this school. The server answers it by writing `verifiedAt` — so a run that
   * comes back with the field still null is a failed verification, not a failed
   * request, and the two are reported differently below.
   *
   * It is refused outright while the connection has never had a successful probe.
   * A verified mapping over a connection nothing has ever answered would be a
   * green tick with nothing behind it.
   */
  const verify = async () => {
    setBusy(true);
    setFailure(null);
    setVerifyMessage(null);
    try {
      const result = await platformApi.institutionIntegrations.verify(institutionId, mapping.id);
      setVerifyMessage(
        result.verifiedAt
          ? t('platform.mappings.verifyOk', { when: result.verifiedAt })
          : t('platform.mappings.verifyFailed'),
      );
      onChanged();
    } catch (cause) {
      setFailure(
        cause instanceof ApiError ? cause : new ApiError((cause as Error).message, 0, mapping.id),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>
          {connection ? (
            <Link
              className={styles.rowTitleLink}
              to={`/admin/integrations/connections/${encodeURIComponent(mapping.platformConnectionId)}`}
            >
              {connection.displayName}
            </Link>
          ) : (
            <Link
              className={styles.rowTitleLink}
              to={`/admin/integrations/connections/${encodeURIComponent(mapping.platformConnectionId)}`}
            >
              {mapping.platformConnectionId}
            </Link>
          )}
        </p>

        {/*
          The three identifiers are listed separately and never merged. They are
          not synonyms: a tenant is the partition the far end routes on, an
          external institution id is the school record inside it, and an
          organisation is the counterparty money settles against. A school can
          share a tenant with its sister campus and still have its own CrediSure
          organisation.
        */}
        <dl className={styles.detailGrid}>
          <dt>{t('platform.mappings.externalTenant')}</dt>
          <dd>{mapping.externalTenantId || t('platform.mappings.identifierNotSet')}</dd>
          <dt>{t('platform.mappings.externalInstitution')}</dt>
          <dd>{mapping.externalInstitutionId || t('platform.mappings.identifierNotSet')}</dd>
          <dt>{t('platform.mappings.externalOrganization')}</dt>
          <dd>{mapping.externalOrganizationId || t('platform.mappings.identifierNotSet')}</dd>
        </dl>

        {connection && (
          <CapabilityChips systemCode={connection.systemCode} capabilities={mapping.capabilities} />
        )}

        <p className={styles.rowMetaLine}>
          {mapping.verifiedAt
            ? t('platform.mappings.verifiedAt', { when: formatDateTime(mapping.verifiedAt, locale) })
            : t('platform.mappings.neverVerified')}
          {mapping.verifiedBy ? ` · ${mapping.verifiedBy}` : ''}
          {mapping.updatedAt ? ` · ${formatRelative(mapping.updatedAt, locale)}` : ''}
        </p>

        {verifyMessage && (
          <p className={styles.rowDetail} role="status">
            {verifyMessage}
          </p>
        )}

        {failure && <FailureNotice error={failure} />}

        <div className={styles.transitionRow}>
          <button type="button" className={styles.secondaryBtn} onClick={verify} disabled={busy}>
            {busy ? t('platform.mappings.verifying') : t('platform.mappings.verify')}
          </button>
          <button type="button" className={styles.linkBtn} onClick={onHistory}>
            {t('platform.mappings.history')}
          </button>
          {mapping.status !== 'DISABLED' && (
            <button type="button" className={styles.linkBtnDanger} onClick={onDisable}>
              {t('platform.mappings.disableForInstitution')}
            </button>
          )}
        </div>
      </div>

      {/*
        Two badges, never one. The upper badge is this institution's mapping; the
        lower is the shared connection's health. Collapsing them would let a green
        connection imply the school is wired up, which is the single most common
        way this screen could lie.
      */}
      <div className={styles.rowAside}>
        <span className={styles.statusPair}>
          <span className={styles.statusPairLabel}>{t('platform.mappings.mappingStatus')}</span>
          <StateBadge kind="integration" value={mapping.status} size="sm" />
        </span>
        <span className={styles.statusPair}>
          <span className={styles.statusPairLabel}>{t('platform.mappings.connectionStatus')}</span>
          {connection ? (
            <StateBadge kind="integration" value={connection.status} size="sm" />
          ) : (
            <span className={styles.mutedTag}>{t('platform.mappings.connectionUnknown')}</span>
          )}
        </span>
      </div>
    </li>
  );
}

/* ─── Add ─── */

function AddMappingDialog({
  open,
  institutionId,
  onClose,
  onCreated,
}: {
  open: boolean;
  institutionId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateInstitutionMappingRequest>(emptyMapping());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connectionsFetcher = useCallback(() => platformApi.connections.list(), []);
  const { state: connectionsState, reload: reloadConnections } = useAsyncData<PlatformConnection[]>(
    'connections:for-mapping',
    connectionsFetcher,
    open,
  );

  useEffect(() => {
    if (open) {
      setForm(emptyMapping());
      setError(null);
    }
  }, [open]);

  const selected =
    connectionsState.status === 'ready'
      ? connectionsState.data.find((connection) => connection.id === form.platformConnectionId)
      : undefined;

  // At least one identifier, or the mapping says nothing. Which one is right
  // depends on the far end, so the form does not insist on a particular field —
  // it insists that the row is not empty.
  const hasIdentifier =
    (form.externalTenantId ?? '').trim().length > 0 ||
    (form.externalInstitutionId ?? '').trim().length > 0 ||
    (form.externalOrganizationId ?? '').trim().length > 0;
  const valid = form.platformConnectionId.length > 0 && hasIdentifier;

  const toggleCapability = (capability: IntegrationCapability) =>
    setForm((current) => {
      const capabilities = current.capabilities ?? [];
      return {
        ...current,
        capabilities: capabilities.includes(capability)
          ? capabilities.filter((entry) => entry !== capability)
          : [...capabilities, capability],
      };
    });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.institutionIntegrations.create(institutionId, {
        platformConnectionId: form.platformConnectionId,
        externalTenantId: form.externalTenantId?.trim() || undefined,
        externalInstitutionId: form.externalInstitutionId?.trim() || undefined,
        externalOrganizationId: form.externalOrganizationId?.trim() || undefined,
        capabilities: form.capabilities,
      });
      onCreated();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.mappings.addTitle')}
      description={t('platform.mappings.addLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.field}>
          <span className={styles.fieldLabel} id="mapping-connection-label">
            {t('platform.mappings.connectionLabel')}
          </span>
          <AsyncPanel
            state={connectionsState}
            onRetry={reloadConnections}
            isEmpty={(data) => data.length === 0}
            emptyTitle={t('platform.mappings.noConnectionsTitle')}
            emptyBody={t('platform.mappings.noConnectionsBody')}
          >
            {(connections) => (
              <Select
                aria-labelledby="mapping-connection-label"
                searchable
                value={form.platformConnectionId}
                onChange={(value) => {
                  const connection = connections.find((entry) => entry.id === value);
                  setForm((current) => ({
                    ...current,
                    platformConnectionId: value,
                    // Seeded from the connection, because a mapping may narrow its
                    // connection's capabilities but never exceed them.
                    capabilities: connection ? [...connection.capabilities] : [],
                  }));
                }}
                placeholder={t('platform.mappings.connectionPlaceholder')}
                options={connections.map((connection) => ({
                  value: connection.id,
                  label: `${connection.displayName} · ${t(`platform.environment.${connection.environment}`)}`,
                }))}
              />
            )}
          </AsyncPanel>
          <span className={styles.fieldHint}>{t('platform.mappings.connectionHint')}</span>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.mappings.externalTenant')}</span>
          <input
            className={styles.input}
            value={form.externalTenantId ?? ''}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, externalTenantId: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.mappings.externalTenantHint')}</span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.mappings.externalInstitution')}</span>
          <input
            className={styles.input}
            value={form.externalInstitutionId ?? ''}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, externalInstitutionId: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.mappings.externalInstitutionHint')}</span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.mappings.externalOrganization')}</span>
          <input
            className={styles.input}
            value={form.externalOrganizationId ?? ''}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, externalOrganizationId: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.mappings.externalOrganizationHint')}</span>
        </label>

        {selected && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.fieldLabel}>{t('platform.mappings.capabilitiesLabel')}</legend>
            <p className={styles.fieldHint}>{t('platform.mappings.capabilitiesHint')}</p>
            <div className={styles.capabilityGrid}>
              {selected.capabilities.map((capability) => (
                <Checkbox
                  key={capability}
                  label={t(`platform.capability.${capability}`, { defaultValue: capability })}
                  checked={(form.capabilities ?? []).includes(capability)}
                  onChange={() => toggleCapability(capability)}
                />
              ))}
            </div>
          </fieldset>
        )}

        {!hasIdentifier && (
          <p className={styles.fieldHint}>{t('platform.mappings.identifierRequired')}</p>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.mappings.addMapping')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyMapping(): CreateInstitutionMappingRequest {
  return {
    platformConnectionId: '',
    externalTenantId: '',
    externalInstitutionId: '',
    externalOrganizationId: '',
    capabilities: [],
  };
}

/* ─── Disable ─── */

function DisableMappingDialog({
  mapping,
  institutionId,
  onClose,
  onDisabled,
}: {
  mapping: InstitutionIntegrationMapping | null;
  institutionId: string;
  onClose: () => void;
  onDisabled: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mapping) return null;

  const run = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.institutionIntegrations.disable(institutionId, mapping.id, reason);
      onDisabled();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmActionDialog
      open
      title={t('platform.mappings.disableTitle')}
      intro={t('platform.mappings.disableIntro', {
        name: mapping.connection?.displayName ?? mapping.platformConnectionId,
      })}
      tone="warning"
      impactStatus="ready"
      impact={
        <ul className={styles.impactList}>
          <li>{t('platform.mappings.disableImpactScope')}</li>
          <li>{t('platform.mappings.disableImpactOthers')}</li>
          <li>{t('platform.mappings.disableImpactKept')}</li>
        </ul>
      }
      confirmLabel={t('platform.mappings.disableForInstitution')}
      submitting={submitting}
      error={error}
      onCancel={onClose}
      onConfirm={run}
    />
  );
}

/* ─── History ─── */

function MappingHistoryDialog({
  mapping,
  institutionId,
  locale,
  onClose,
}: {
  mapping: InstitutionIntegrationMapping | null;
  institutionId: string;
  locale: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mappingId = mapping?.id ?? '';
  const fetcher = useCallback(
    () => platformApi.institutionIntegrations.history(institutionId, mappingId),
    [institutionId, mappingId],
  );
  const { state, reload } = useAsyncData<PlatformAuditRecord[]>(
    `mapping-history:${institutionId}:${mappingId}`,
    fetcher,
    mappingId.length > 0,
  );

  if (!mapping) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.mappings.historyTitle')}
      description={t('platform.mappings.historyLead')}
    >
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.mappings.historyEmpty')}
      >
        {(data) => (
          <ol className={styles.timeline}>
            {data.map((entry) => (
              <li key={entry.id} className={styles.timelineItem}>
                <span className={styles.timelineDot} aria-hidden="true" />
                <div>
                  <p className={styles.rowTitle}>
                    {t(`platform.mappings.historyAction.${entry.action}`, {
                      defaultValue: entry.action,
                    })}
                  </p>
                  <p className={styles.rowMetaLine}>
                    {formatDateTime(entry.occurredAt, locale)}
                    {entry.actorName || entry.actorId ? ` · ${entry.actorName ?? entry.actorId}` : ''}
                  </p>
                  {entry.reason && <p className={styles.rowDetail}>{entry.reason}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </AsyncPanel>
    </Modal>
  );
}

/* ─── The registry this replaces ─── */

/**
 * The old per-institution registrations, still real and still carrying data.
 *
 * Kept visible, and kept clearly secondary, for the length of the migration. The
 * rows above are the new model; these are the ones that exist today, with their
 * own connection details and their own entity mappings. Hiding them would make an
 * operator believe an institution has no integrations at all while records are
 * still syncing under a registration nobody can see.
 */
function LegacyRegistrationsPanel({
  institutionId,
  locale,
}: {
  institutionId: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.integrations.list(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<IntegrationRegistration[]>(
    `legacy-integrations:${institutionId}`,
    fetcher,
  );

  return (
    <Panel title={t('platform.mappings.legacyTitle')} icon="history">
      <p className={styles.fieldHint}>{t('platform.mappings.legacyLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.mappings.legacyEmpty')}
      >
        {(data) => (
          <ul className={styles.rowList}>
            {data.map((registration) => (
              <li key={registration.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    <Link
                      className={styles.rowTitleLink}
                      to={`/admin/integrations/${encodeURIComponent(registration.id)}`}
                    >
                      {registration.displayName}
                    </Link>
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t(`platform.integrations.connector.${registration.connectorType}`, {
                      defaultValue: registration.connectorType,
                    })}
                    {` · ${registration.code}`}
                    {registration.lastSyncAt
                      ? ` · ${t('platform.integrations.lastSync', {
                          when: formatRelative(registration.lastSyncAt, locale),
                        })}`
                      : ''}
                  </p>
                </div>
                <div className={styles.rowAside}>
                  <StateBadge kind="integration" value={registration.state} size="sm" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Panel>
  );
}
